import { COMMON_AMPLITUDE_CONFIG } from "@common-shared/config/amplitude.config"
import { COMMON_ONE_VERIFY_DEMO_CONFIG } from "@common-shared/config/oneVerify/oneVerifyDemo.config"
import { OneVerifyRouteHelper } from "@common-shared/helpers/oneVerifyRoute.helper"
import type { IIndexable } from "@common-shared/types/general.types"

import { HttpHelper as NirvanaHttpHelper } from "@front-shared/helpers/http.helper"
import { ToastrHelper } from "@front-shared/helpers/toastr.helper"
import type { AvailableHttpMethod } from "@front-shared/types/general"

import { GENERAL_CONFIG } from "../config/general.config"
import { AmplitudeHelper } from "./amplitude.helper"
import { AuthHelper } from "./auth.helper"
import { DemoHelper } from "./demo.helper"
import { LogoutHelper } from "./logout.helper"

export class HttpHelper {
    /**
     * Send request to server
     */
    static async sendRequest(
        url: string,
        data: IIndexable = {},
        method: AvailableHttpMethod = "GET",
        headers: IIndexable = {},
        appendApiUrl: string | undefined = import.meta.env.VITE_API_URL,
        errorToastrTimeout = GENERAL_CONFIG.defaultToastrTimeout,
        ignoreToastrErrorCodes: number[] = [],
        abortSignal?: AbortSignal,
    ): Promise<any> {
        headers = Object.assign({}, headers)

        if (await AuthHelper.isLoggedIn()) {
            // Add Amplitude device ID to headers if available (only for logged in users)
            headers[COMMON_AMPLITUDE_CONFIG.amplitudeDeviceIdHeader] =
                AmplitudeHelper.getInstance()?.getDeviceId()

            const adminViewAsData = AuthHelper.getEagleEyeViewAsData()

            // Override what current user will see (practice and therapist data), aka God mode
            if (adminViewAsData) {
                headers[GENERAL_CONFIG.eagleEye.headers.userId] =
                    adminViewAsData.user?.id

                headers[GENERAL_CONFIG.eagleEye.headers.practiceId] =
                    adminViewAsData.practice?.id
            }
        }

        if (HttpHelper.blockDemoMutationIfNeeded(url, method)) {
            // Match failed-request shape so callers treat this as a soft failure
            // without a second toast from a thrown Error message.
            return {}
        }

        return await NirvanaHttpHelper.sendRequest(
            url,
            data,
            method,
            appendApiUrl,
            headers,
            await AuthHelper.getAccessToken(),
            errorToastrTimeout,
            [...ignoreToastrErrorCodes, 401],
            ToastrHelper.error,
            undefined,
            () => {
                LogoutHelper.logout(
                    null,
                    undefined,
                    undefined,
                    undefined,
                    "Your session has expired. Please log in again.",
                )
            },
            undefined,
            undefined,
            undefined,
            abortSignal,
            AuthHelper.isDemoSessionActive() ? 0 : undefined,
        )
    }

    /**
     * Block demo sessions from non-GET requests outside the shared allowlist.
     * Uses the same OneVerifyRouteHelper rules as backend auth middleware.
     * Returns true when the request was blocked (caller should return {}).
     */
    private static blockDemoMutationIfNeeded(
        url: string,
        method: AvailableHttpMethod,
    ): boolean {
        if (!AuthHelper.isDemoSessionActive()) {
            return false
        }

        if (
            OneVerifyRouteHelper.isRouteAllowed(
                method,
                url,
                COMMON_ONE_VERIFY_DEMO_CONFIG.allowedMutationRoutes,
            )
        ) {
            return false
        }

        // Callers treat the empty response as a failure and toast their own
        // message ("Request failed", "Failed to add rules", ...), which would
        // stack on top of the warning below.
        ToastrHelper.suppressErrors(
            GENERAL_CONFIG.demoBlockedMutationErrorToastSuppressionMs,
        )

        ToastrHelper.warning(
            DemoHelper.getBlockedMutationMessage(url),
            undefined,
            false,
            true,
        )

        return true
    }
}
